import { Box, Typography, Autocomplete, TextField, Chip, Stack } from "@mui/material";
import { ChangeEvent } from "react";
import { Metric, metricGroupPresets } from "../../../models/Explorer";
import { useExplorerStore } from "../../../data/ExplorerStore";
import { useGlossary } from "../../../hooks/useGlossary";
import QueryForm from "./QueryForm";

// First 4 presets are team-level, last 3 are individual
const teamPresets = metricGroupPresets.slice(0, 4);
const individualPresets = metricGroupPresets.slice(4);

const chipSx = (checked: boolean) => ({
  borderRadius: "3px",
  fontSize: "0.7rem",
  height: "24px",
  fontWeight: checked ? 600 : 400,
  backgroundColor: checked ? "#757575" : "transparent",
  color: checked ? "#fff" : "inherit",
  borderColor: "#bdbdbd",
  "&:hover": {
    backgroundColor: checked ? "#616161" : "#eeeeee",
  },
}) as const;


interface Props {
  i: number;
}

export default function MetricGroupForm({ i }: Props) {
  const metrics = useExplorerStore(({ metrics }) => metrics);
  const { byName: glossary } = useGlossary();
  const metricGroup = useExplorerStore(({ metricGroups }) => metricGroups[i]);
  const updateMetricGroup = useExplorerStore(
    ({ updateMetricGroup }) => updateMetricGroup
  );
  const metricGroupForUpdate = Object.assign({}, metricGroup);

  function onMetricsChange(
    event: ChangeEvent<unknown>,
    value: string[] | null
  ) {
    const newMetrics =
      value && value.length
        ? value?.map((m) => ({ value: m, label: m }))
        : undefined;
    metricGroupForUpdate.metrics = newMetrics;
    updateMetricGroup(i, metricGroupForUpdate);
  }

  function onNameChange(event: ChangeEvent<HTMLInputElement>) {
    // Same characters the backend keeps (lib/query.py metric_group_name).
    const name = event.target.value.replace(/[^\w\- ]/g, "").slice(0, 40);
    metricGroupForUpdate.name = name || undefined;
    updateMetricGroup(i, metricGroupForUpdate);
  }

  function onRatioChange(event: ChangeEvent<unknown>, value: Metric | null) {
    metricGroupForUpdate.ratio = value;
    updateMetricGroup(i, metricGroupForUpdate);
  }

  function addMetric(metric: Metric) {
    const included = metricGroupForUpdate.metrics?.some(
      (m) => m.value === metric.value
    );
    if (!included) {
      if (!metricGroupForUpdate.metrics?.length) {
        metricGroupForUpdate.metrics = [metric];
      } else {
        metricGroupForUpdate.metrics = [
          ...metricGroupForUpdate.metrics,
          metric,
        ];
      }
    }
  }

  function removeMetric(metric: Metric) {
    const index = metricGroupForUpdate.metrics?.findIndex(
      (m) => m.value === metric.value
    );
    if (index && index !== -1) {
      const metricsForUpdate = metricGroupForUpdate.metrics?.slice() ?? [];
      metricsForUpdate?.splice(index, 1);
      metricGroupForUpdate.metrics = metricsForUpdate;
    }
  }

  function presetToggle(preset: typeof metricGroupPresets[number]) {
    const allIncluded = preset.metrics.every((m) =>
      metricGroupForUpdate.metrics?.some
        ? metricGroupForUpdate.metrics.some((m2) => m.value === m2.value)
        : false
    );
    if (allIncluded) {
      preset.metrics.forEach((metric) => removeMetric(metric));
    } else {
      preset.metrics.forEach((metric) => addMetric(metric));
    }
    updateMetricGroup(i, metricGroupForUpdate);
  }

  function isPresetChecked(preset: typeof metricGroupPresets[number]) {
    return preset.metrics.every((m) =>
      metricGroupForUpdate.metrics?.some
        ? metricGroupForUpdate.metrics.some((m2) => m.value === m2.value)
        : false
    );
  }

  return (
    <>
      <Box mb={2}>
        <Typography mb={0.5} fontSize={15} fontWeight={600}>
          Name
        </Typography>
        <TextField
          id={`metricGroup${i}--name`}
          value={metricGroupForUpdate.name ?? ""}
          onChange={onNameChange}
          placeholder="Optional: prefixes this group's columns (keep → keep_iAST%)"
          size="small"
          sx={{ width: { xs: "100%", sm: "360px" } }}
        />
      </Box>
      <Box mb={2}>
        <Typography mb={0.5} fontSize={15} fontWeight={600}>
          Metrics
        </Typography>
        <Autocomplete
          onChange={onMetricsChange}
          id={`metricGroup${i}--metric-select`}
          multiple
          options={metrics}
          value={metricGroupForUpdate.metrics?.map ? metricGroupForUpdate.metrics.map((m) => m.value) : []}
          renderOption={(props, option) => {
            // The formula is the useful hint for a metric: it says exactly what is
            // divided by what, which is the question behind most metric confusion.
            const entry = glossary.get(option);
            const hint = entry?.kind === "metric" ? entry.formula : undefined;
            return (
              <li {...props} key={option}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: "0.8rem" }}>{option}</Typography>
                  {hint && (
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        color: "rgba(0,0,0,0.55)",
                        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {hint}
                    </Typography>
                  )}
                </Box>
              </li>
            );
          }}
          renderInput={(params) => <TextField {...params} variant="outlined" placeholder="Select metrics" />}
          blurOnSelect={false}
          sx={{ width: "100%" }}
        />
        <Stack mt={1} spacing={0.5}>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {teamPresets.map((preset) => {
              const checked = isPresetChecked(preset);
              return (
                <Chip
                  key={preset.label}
                  label={preset.label}
                  variant={checked ? "filled" : "outlined"}
                  onClick={() => presetToggle(preset)}
                  sx={chipSx(checked)}
                />
              );
            })}
          </Stack>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {individualPresets.map((preset) => {
              const checked = isPresetChecked(preset);
              return (
                <Chip
                  key={preset.label}
                  label={preset.label}
                  variant={checked ? "filled" : "outlined"}
                  onClick={() => presetToggle(preset)}
                  sx={chipSx(checked)}
                />
              );
            })}
          </Stack>
        </Stack>
      </Box>
      <QueryForm metricGroupIndex={i} />
      <Box mb={2}>
        <Typography mb={0.5} fontSize={15} fontWeight={600}>
          Ratio
        </Typography>
        <Autocomplete
          onChange={onRatioChange}
          id={`metricGroup${i}--ratio-select`}
          options={metricGroupForUpdate.metrics ?? []}
          value={metricGroupForUpdate.ratio ?? null}
          renderInput={(params) => (
            <TextField {...params} variant="outlined" placeholder="Select" size="small" />
          )}
          isOptionEqualToValue={(option, value) => {
            if (!value) return false;
            return option.value === value.value;
          }}
          size="small"
          sx={{ width: { xs: "100%", sm: "200px" } }}
        />
      </Box>
    </>
  );
}
